import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';
import styled from '@emotion/styled';

import { useToggle } from '@hooks/toggle/useToggle';
import { useGetUserSaleItemsFetch, usePatchUserSaleItemsFetch } from '@hooks/fetch/useUserFetch';
import { getCurrentItem } from '@components/_templates/seller/SellerContent/Hooks/getCurrentItem';
import { getExistMaterialIdx } from '@components/_templates/seller/SellerContent/Hooks/isExistMaterial';

import Button from '@components/_atoms/Button';
import DeleteAllModal from './Modal/DeleteAllModal';
import SearchProductCard from '@components/_organism/service/SearchProductCard';
import FloatingButtonSection from '@components/_molecules/FloatingButtonSection';
import ProductAlarmLabelTable from './ProductAlarmLabelTable';
import { Toast } from '@components/_atoms/Toast';
import { AlarmProductType } from '@components/_templates/seller/SellerPromoteAlramContent/types';

import ButtonCode from '@constants/log/buttonCode';
import { ButtonColor } from '@constants/atoms';
import { MYPAGE } from '@constants/routes/routes';
import { LAPTOP_MODE, MOBILE_MODE } from '@constants/size';
import {
  deleteSaleProductListState,
  SALE_PRODUCT_SEARCH_FIELD,
  saleProductAlarmIsUserSaleMaterialPushState,
  saleProductOptionAtom,
} from '@recoil/service/SaleProductAlarm';

const Layout = styled.div`
  @media (${LAPTOP_MODE}) {
    padding: 0 40px;
  }
  @media (${MOBILE_MODE}) {
    padding: 0;
  }
  overflow: hidden;
`;

export default function MaterialAlarmEditForm() {
  const router = useRouter();
  const deleteAllModal = useToggle();

  // recoil
  const [saleProductTypeOption] = useRecoilState(saleProductOptionAtom);
  const [deleteSaleProductList, setDeleteSaleProductList] = useRecoilState(deleteSaleProductListState);
  const saleProductAlarmIsUserSaleMaterialPush = useRecoilValue(saleProductAlarmIsUserSaleMaterialPushState);

  // reset
  const resetSaleProductOption = useResetRecoilState(saleProductOptionAtom);
  const resetDeleteSaleProductListState = useResetRecoilState(deleteSaleProductListState);
  const resetSaleProductAlarmIsUserSaleMaterialPush = useResetRecoilState(saleProductAlarmIsUserSaleMaterialPushState);

  const [addedSaleItemList, setAddedSaleItemList] = useState<AlarmProductType[]>([]);
  const [userSaleItemList, setUserSaleItemList] = useState<AlarmProductType[]>([]);

  const { userSaleItemsFetchData, userSaleItemsFetchIsLoading } = useGetUserSaleItemsFetch();
  const { patchUserSaleItemsFetchMutate } = usePatchUserSaleItemsFetch();

  const productList = useMemo(() => {
    return [...addedSaleItemList, ...(userSaleItemList || [])];
  }, [userSaleItemList, addedSaleItemList]);

  const handleClickMaterial = (material: string, field?: SALE_PRODUCT_SEARCH_FIELD) => {
    if (!field) return;
    const existItemidx = getExistMaterialIdx(material, field, productList, saleProductTypeOption.searchCondition);

    if (existItemidx > -1) {
      handleDeleteAlarmProduct(existItemidx);
    } else {
      const currentItem = getCurrentItem(material, field, saleProductTypeOption.searchCondition);
      setAddedSaleItemList((prev) => [...prev, currentItem]);
    }
  };

  const handleDeleteAlarmProduct = (index: number) => {
    const isAddedSaleItem = index < addedSaleItemList.length;
    const productIndex = isAddedSaleItem ? index : index - addedSaleItemList.length;

    //index는 전체 리스트의 index
    if (isAddedSaleItem) {
      const filteredAddedSaleItemList = addedSaleItemList.filter((saleItem, index) => {
        return index !== productIndex;
      });
      setAddedSaleItemList(filteredAddedSaleItemList);
    } else {
      const targetUserSaleItem = userSaleItemList[productIndex];
      const filteredUserSaleItemList = userSaleItemList.filter((saleItem, index) => {
        return index !== productIndex;
      });
      setUserSaleItemList(filteredUserSaleItemList);
      setDeleteSaleProductList((prev) => [...prev, targetUserSaleItem.id!]);
    }
  };

  const openDeleteModal = () => {
    if (productList.length) {
      deleteAllModal.on();
    }
  };

  const handleClickDeleteAll = () => {
    const deletedSaleProductList = userSaleItemList.map((userSaleItem) => {
      return userSaleItem.id!;
    });
    setDeleteSaleProductList(deletedSaleProductList);

    setAddedSaleItemList([]);
    setUserSaleItemList([]);
    deleteAllModal.off();
  };

  //삭제할 자재 목록 -> 유저가 생성했던 자재가 삭제될경우 삭제목록에 추가
  //생성할 자재 목록 ->
  const handleClickSave = () => {
    const createSaleMaterialList = addedSaleItemList.map((addedSaleItem) => {
      const { product_type, category, type1, material, standard_code } = addedSaleItem;
      return {
        product_type,
        standard_code: standard_code,
        category: category ?? undefined,
        type1,
        material,
      };
    });

    patchUserSaleItemsFetchMutate(
      {
        is_user_sale_material_push: saleProductAlarmIsUserSaleMaterialPush,
        delete_sale_material_list: deleteSaleProductList,
        create_sale_material_list: createSaleMaterialList,
      },
      {
        onSuccess: () => {
          Toast.success('변경 내용을 저장했어요');
          router.push(MYPAGE);
        },
      },
    );
  };

  //페이지 didmount시 유저가 등록했던 판매자재 세팅
  useEffect(() => {
    if (!userSaleItemsFetchIsLoading) {
      setUserSaleItemList(userSaleItemsFetchData as AlarmProductType[]);
    }
  }, [userSaleItemsFetchIsLoading]);

  //페이지 unmount시 state reset
  useEffect(() => {
    return () => {
      resetSaleProductAlarmIsUserSaleMaterialPush();
      resetDeleteSaleProductListState();
      resetSaleProductOption();
    };
  }, []);

  return (
    <Layout>
      <SearchProductCard isShowAlarmSetter={true} handleClickMaterial={handleClickMaterial} productList={productList} />
      <ProductAlarmLabelTable
        productList={productList}
        onClose={handleDeleteAlarmProduct}
        deleteAll={openDeleteModal}
      />
      <FloatingButtonSection
        button1={
          <Button
            text="이전으로"
            color={ButtonColor.OutlinedBlack}
            onClick={router.back}
            steelbosoId={ButtonCode.BTN_BACK}
          />
        }
        button2={<Button text="저장하기" onClick={handleClickSave} steelbosoId={ButtonCode.BTN_SAVE} />}
      />

      {deleteAllModal.isToggled && (
        <DeleteAllModal
          isOpen={deleteAllModal.isToggled}
          onClose={deleteAllModal.off}
          handleDeleteAll={handleClickDeleteAll}
        />
      )}
    </Layout>
  );
}
